import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

// Generate print-friendly HTML for Doctor Opinion (consistent with prescription layout)
export const generateDoctorOpinionPrintHTML = (opinionData, patientData, doctorUser) => {
  const docName = doctorUser?.name || `${doctorUser?.firstName || ''} ${doctorUser?.lastName || ''}`.trim() || 'Doctor';
  const specialty = doctorUser?.specialty || doctorUser?.speciality || opinionData?.speciality || '';
  const patientName = [patientData?.firstName || patientData?.name || '', patientData?.lastName || ''].filter(Boolean).join(' ') || 'Unknown Patient';

  return `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <title>Doctor Opinion</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; background: #fff; color: #000; line-height: 1.6; font-size: 14px; }
        .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 20px; margin-top: 30px; margin-bottom: 20px; }
        .header h1 { color: #2563eb; margin: 0; font-size: 28px; }
        .hospital { text-align: center; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 2px solid #0066cc; }
        .hospital h1 { color: #0066cc; margin: 0; font-size: 24px; }
        .hospital p { color: #666; margin: 5px 0; }
        .date { margin: 5px 0; color: #666; }
        .section { margin-bottom: 20px; }
        .section h2 { color: #333; font-size: 18px; margin-bottom: 10px; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
        .meta p { margin: 4px 0; }
        .label { font-weight: 600; }
        .box { border: 1px solid #ddd; padding: 12px; border-radius: 8px; background: #fff; }
        pre { font-family: Arial, sans-serif; font-size: 14px; margin: 0; white-space: pre-wrap; }
        .signature { margin-top: 40px; border-top: 1px solid #ccc; padding-top: 20px; text-align: right; }
        .signature-line { border-bottom: 1px solid #333; width: 150px; display: inline-block; margin-left: 10px; }
        @media print { body { margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
      </style>
    </head>
    <body>
      <div class="hospital">
        <h1>HealthCare Plus Hospital</h1>
        <p>Excellence in Healthcare | 24/7 Emergency Services</p>
        <p>123 Medical Center Drive, Healthcare City | +1-800-HEALTH | www.healthcareplus.com</p>
      </div>
      <div class="header">
        <h1>Doctor Opinion</h1>
        <p class="date">Date: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}</p>
      </div>
      <div class="section">
        <h2>Details</h2>
        <div class="meta">
          <p><span class="label">Patient:</span> ${patientName}</p>
          <p><span class="label">Doctor:</span> ${docName}${specialty ? ' - ' + specialty : ''}</p>
          ${opinionData?.toDoctorName ? `<p><span class="label">To:</span> ${opinionData.toDoctorName}</p>` : ''}
        </div>
      </div>
      <div class="section">
        <h2>Opinion / Notes</h2>
        <div class="box"><pre>${(opinionData?.notes || '').replace(/[&<>]/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;'}[c]))}</pre></div>
      </div>
      <div class="signature">
        <p><strong>Doctor's Signature</strong></p>
        <p><span class="signature-line"></span></p>
      </div>
    </body>
    </html>
  `;
};

// Generate PDF for Doctor Opinion using html2canvas -> jsPDF (consistent with prescription implementation)
export const generateDoctorOpinionPDF = async (opinionData, patientData, doctorUser) => {
  try {
    const html = generateDoctorOpinionPrintHTML(opinionData, patientData, doctorUser);

    // Create off-screen container
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-10000px';
    container.style.top = '0';
    container.style.width = '800px';
    container.style.background = '#ffffff';
    container.innerHTML = html;
    document.body.appendChild(container);

    // Wait a bit to ensure fonts/styles apply
    await new Promise(r => setTimeout(r, 300));

    // Render to canvas
    const canvas = await html2canvas(container, { scale: 2, useCORS: true, backgroundColor: '#ffffff', width: 800 });
    const imgData = canvas.toDataURL('image/png');

    // Compose PDF pages
    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgWidth = 210; // A4 width in mm
    const pageHeight = 295; // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    const patientName = [patientData?.firstName || patientData?.name || '', patientData?.lastName || ''].filter(Boolean).join('_') || 'patient';
    const fileName = `doctor-opinion_${patientName}_${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(fileName);

    document.body.removeChild(container);
    return { success: true, fileName };
  } catch (error) {
    console.error('Error generating Doctor Opinion PDF:', error);
    return { success: false, error: error.message };
  }
};
